import { format, parse, differenceInCalendarDays } from 'date-fns'
import { it, enGB, de } from 'date-fns/locale'
import type { Locale } from './api/types'

const DF_LOCALE = { it, en: enGB, de }

/** "YYYY-MM-DD" -> Date (local, no tz shift) */
export function parseDay(day: string): Date {
  return parse(day, 'yyyy-MM-dd', new Date())
}

/** Date -> "YYYY-MM-DD" */
export function toDay(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

/**
 * Today as "YYYY-MM-DD" in the garage's timezone, not the visitor's — a customer booking from
 * Tokyo is already on tomorrow's date while Venice is still on today's, and the backend validates
 * arrival days against Europe/Rome.
 */
export function garageToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
}

/** True when a "YYYY-MM-DD" day already lies in the past (garage time). */
export function isPastDay(day: string | null | undefined): boolean {
  if (!day) return false
  return day < garageToday()
}

/** Current time in the garage's timezone as "HH:MM:SS". */
export function garageNowTime(): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Rome',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date())
}

/**
 * True when an arrival selection has gone by: an earlier day, or today at an hour already passed.
 * Both are rejected by the backend ("FromDay is invalid"), so they are worth clearing rather than
 * letting the funnel resubmit them.
 */
export function isPastSelection(day: string | null | undefined, hour?: string | null): boolean {
  if (!day) return false
  if (isPastDay(day)) return true
  if (!hour || day !== garageToday()) return false
  return hour.slice(0, 8) < garageNowTime()
}

/** "HH:MM:SS" -> "HH:MM" */
export function hhmm(time: string): string {
  return time?.slice(0, 5) ?? ''
}

/** Pretty day, e.g. "mar 10 giu 2026" */
export function prettyDay(day: string, locale: Locale = 'it'): string {
  if (!day) return ''
  return format(parseDay(day), 'EEE d MMM yyyy', { locale: DF_LOCALE[locale] })
}

export function prettyDayShort(day: string, locale: Locale = 'it'): string {
  if (!day) return ''
  return format(parseDay(day), 'd MMM', { locale: DF_LOCALE[locale] })
}

/** Pretty weekday + day, e.g. "dom 8 giu" (no year). */
export function prettyDayWeekday(day: string, locale: Locale = 'it'): string {
  if (!day) return ''
  return format(parseDay(day), 'EEE d MMM', { locale: DF_LOCALE[locale] })
}

/** Whole calendar nights between two "YYYY-MM-DD" days (min 0; 0 = same day). */
export function nightsBetween(fromDay: string, toDay: string): number {
  if (!fromDay || !toDay) return 0
  return Math.max(0, differenceInCalendarDays(parseDay(toDay), parseDay(fromDay)))
}

/** Build the list of selectable time slots for a day given the subdivision (minutes). */
export function buildTimeSlots(subdivisionMinutes: number, minTime?: string): string[] {
  const step = subdivisionMinutes > 0 ? subdivisionMinutes : 30
  const slots: string[] = []
  const minMinutes = minTime ? toMinutes(minTime) : 0
  for (let m = 0; m < 24 * 60; m += step) {
    if (m < minMinutes) continue
    const h = Math.floor(m / 60)
    const mm = m % 60
    slots.push(`${pad(h)}:${pad(mm)}:00`)
  }
  return slots
}

function toMinutes(time: string): number {
  const [h, m] = time.split(':').map((n) => parseInt(n, 10))
  return h * 60 + m
}
function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}
